import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import type { CompatibilityEntry, PanelCatalogEntry } from '../api/types.ts';
import { ChoiceGroup, Notice } from '../components/ui.tsx';
import { isPanelSupportedForPreset, type WizardState } from './state.ts';

const DIFFICULTY: Record<PanelCatalogEntry['difficulty'], MessageKey> = {
  easy: 'wizard.panel.difficultyEasy',
  moderate: 'wizard.panel.difficultyModerate',
  advanced: 'wizard.panel.difficultyAdvanced',
};

/**
 * Choosing a panel.
 *
 * Every word here comes from the adapter's own descriptor, so the picker
 * cannot promise something the planner will then refuse. Where a panel is
 * ruled out by a choice already made, it is shown greyed with the reason
 * rather than removed — a missing option is a mystery.
 */
export function PanelStep({
  panels,
  compatibility,
  state,
  onChange,
}: {
  panels: readonly PanelCatalogEntry[];
  compatibility: readonly CompatibilityEntry[];
  state: WizardState;
  onChange: (state: WizardState) => void;
}): ReactNode {
  const { t, pick, formatNumber } = useI18n();
  const chosen = panels.find((panel) => panel.adapterId === state.panelAdapterId);
  const notes = chosen?.securityNotes ?? [];

  /**
   * The one to pick if you do not know which to pick.
   *
   * This is the first decision in the product and, for somebody who rented a
   * server last week, the least answerable: three cards, each with protocols,
   * maturity and a memory figure. The adapter descriptors already carry
   * `difficulty`, which exists to say exactly this, so the simplest usable one
   * that is still available gets the star the tunnel step's "choose for me"
   * already gets.
   *
   * A recommendation, not a decision: every panel stays selectable, nothing is
   * pre-selected, and the marked one is marked for a stated reason.
   */
  const recommended = panels.find(
    (panel) =>
      panel.difficulty === 'easy' &&
      (state.preset === undefined ||
        isPanelSupportedForPreset(compatibility, panel.adapterId, state.preset)),
  );

  return (
    <>
      <ChoiceGroup
        legend={t('wizard.step.panel')}
        help={
          recommended
            ? `${t('wizard.panel.help')} ${t('wizard.panel.recommended', { name: recommended.name })}`
            : t('wizard.panel.help')
        }
        value={state.panelAdapterId}
        onChange={(panelAdapterId) => onChange({ ...state, panelAdapterId })}
        options={panels.map((panel) => {
          const unsupported =
            state.preset !== undefined &&
            !isPanelSupportedForPreset(compatibility, panel.adapterId, state.preset);
          return {
            value: panel.adapterId,
            title: panel.name,
            description: pick(panel.recommendedFor) || pick(panel.description),
            meta: (
              <>
                {t(DIFFICULTY[panel.difficulty])}
                {' · '}
                {t(panel.multiNode ? 'wizard.panel.multiNode' : 'wizard.panel.singleNode')}
                {' · '}
                {t('wizard.panel.needs', {
                  memory: formatNumber(panel.recommendedMemoryMb),
                  cpu: formatNumber(panel.recommendedCpuCount),
                })}
              </>
            ),
            disabled: unsupported,
            disabledReason: unsupported ? t('wizard.panel.unavailableForShape') : undefined,
            recommended: !unsupported && panel.adapterId === recommended?.adapterId,
          };
        })}
      />

      {/* Shown the moment the panel is chosen, not buried in the review: a
        caveat that changes somebody's mind should reach them while they are
        still choosing. */}
      {notes.map((note) => (
        <Notice key={note.code} tone="warning" title={pick(note.title)}>
          {pick(note.detail)}
        </Notice>
      ))}
    </>
  );
}
